import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FeatherIcon from '../../common/FeatherIcon';
import useHeaderNotifications from './useHeaderNotifications';
import {
  NOTIF_MODULES,
  badgeText,
  buildNotifications,
  classifyAlert,
  groupNotifications,
  relTime,
} from './notificationSources';

/**
 * The bell and its Notifications panel.
 *
 * "Not alerts but notifications": everything across the platform that is for
 * this person and that they can open — approvals routed to them and findings
 * assigned to them ("Needs you"), plus unread alerts from the modules they are
 * entitled to. Sources and their contracts are documented in
 * notificationSources.js; fetching lives in useHeaderNotifications.js.
 */
const NotificationsPanel = ({ email = '', canOpenSection, canOpenModule }) => {
  const { t: translate } = useTranslation('layout');
  const tn = useCallback(
    (key, opts) => translate(`layout:headerNotifications.${key}`, opts),
    [translate],
  );

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('all');          // 'all' | 'needs'
  const [moduleFilter, setModuleFilter] = useState('all');
  const wrapRef = useRef(null);
  const bellRef = useRef(null);
  const panelRef = useRef(null);
  const tabRefs = useRef({});

  const securityOpen = canOpenSection('security');
  const {
    approvals, alerts, recs, recsEnabled, marking, refresh, retry, markAllRead, markRecRead,
  } = useHeaderNotifications({ email, securityOpen });

  const items = useMemo(() => buildNotifications({
    approvals: approvals.items || [],
    recs: recs.items || [],
    alerts: alerts.items || [],
    canOpenSection,
    t: tn,
  }), [approvals.items, recs.items, alerts.items, canOpenSection, tn]);

  // Counts. Approvals cannot be "read", so each one counts until it is decided.
  const needsCount = items.filter((n) => n.needsYou).length;
  const unreadCount = items.filter((n) => n.unread).length;
  const alertsShown = items.filter((n) => n.kind === 'alert').length;
  // "+" only when the server holds more than were loaded AND nothing loaded was
  // filtered out as unopenable — otherwise the extra may not be this person's.
  const more = approvals.more
    || (alerts.more && alertsShown === (alerts.items || []).length);
  const badge = badgeText(unreadCount, more);

  const loading = approvals.items === null && alerts.items === null && recs.items === null;
  const errors = [
    approvals.error && { id: 'approvals', label: tn('loadFailedApprovals'), retry: retry.approvals },
    alerts.error && { id: 'alerts', label: tn('loadFailedAlerts'), retry: retry.alerts },
    recsEnabled && recs.error && { id: 'recs', label: tn('loadFailedRecs'), retry: retry.recs },
  ].filter(Boolean);

  // Chips: only modules this person can open.
  const chips = useMemo(() => NOTIF_MODULES.filter((m) => canOpenModule(m)), [canOpenModule]);
  useEffect(() => {
    if (moduleFilter !== 'all' && !chips.some((m) => m.id === moduleFilter)) setModuleFilter('all');
  }, [chips, moduleFilter]);

  const visible = items.filter((n) => (tab === 'needs' ? n.needsYou : true)
    && (moduleFilter === 'all' || n.module.id === moduleFilter));
  const groups = groupNotifications(visible);

  const close = useCallback((returnFocus = false) => {
    setOpen(false);
    if (returnFocus && bellRef.current) bellRef.current.focus();
  }, []);

  const toggle = () => {
    if (!open) refresh();
    setOpen(!open);
  };

  // Click outside and Escape close the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) close(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); close(true); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  // Move focus into the panel when it opens, so keyboard users land in it.
  useEffect(() => {
    if (open && panelRef.current) panelRef.current.focus();
  }, [open]);

  const onTabKey = (e) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next = tab === 'all' ? 'needs' : 'all';
    setTab(next);
    tabRefs.current[next]?.focus();
  };

  const canMark = items.some((n) => n.unread && (n.kind === 'alert' || n.kind === 'rec'));
  const onMarkAll = () => {
    markAllRead({
      alertIds: items.filter((n) => n.kind === 'alert' && n.unread)
        .map((n) => classifyAlert(n.raw).id).filter((id) => id != null),
      recIds: items.filter((n) => n.kind === 'rec' && n.unread).map((n) => n.raw.id),
    });
  };

  const onOpenItem = (n) => {
    if (n.kind === 'rec' && n.unread) markRecRead(n.raw.id);
    close(false);
  };

  const groupTitle = { needs: tn('groupNeedsYou'), today: tn('groupToday'), earlier: tn('groupEarlier') };
  const moduleLabel = (m) => tn(m.labelKey, { defaultValue: m.label });
  const activeChip = chips.find((m) => m.id === moduleFilter);

  const renderRow = (n) => {
    const style = { '--nm-color': n.module.color, '--nm-tint': n.module.tint };
    const when = relTime(n.ts, tn);
    const body = (
      <>
        <span className="hdr-notif-ic" aria-hidden="true"><FeatherIcon name={n.module.icon} size={16} /></span>
        <span className="hdr-notif-main">
          <span className="hdr-notif-meta">
            <span className="hdr-notif-mod">{moduleLabel(n.module)}</span>
            {when && <span className="hdr-notif-when">· {when}</span>}
            {n.due && <span className="hdr-notif-due">· {n.due}</span>}
          </span>
          <span className="hdr-notif-title">{n.title}</span>
          {n.sub && <span className="hdr-notif-sub">{n.sub}</span>}
          {n.needsYou && (
            <span className="hdr-notif-actions">
              <Link className="hdr-notif-btn is-primary" to={n.primary.to} onClick={() => onOpenItem(n)}>
                {n.primary.label}
              </Link>
              {n.open && (
                <Link className="hdr-notif-btn" to={n.open.to} onClick={() => onOpenItem(n)}>
                  {n.open.label}
                </Link>
              )}
            </span>
          )}
        </span>
        <span className="hdr-notif-dot" aria-hidden="true" />
        {n.unread && <span className="visually-hidden">{tn('unread')}</span>}
      </>
    );
    const cls = `hdr-notif-row${n.unread ? ' is-unread' : ''}`;
    // A row with its own buttons cannot also be one big link (no nested
    // interactive content), so only the plain alert rows are whole-row links.
    return n.needsYou
      ? <li key={n.key} className={cls} style={style}>{body}</li>
      : (
        <li key={n.key} style={style}>
          <Link className={cls} to={n.open.to} onClick={() => onOpenItem(n)}>{body}</Link>
        </li>
      );
  };

  let emptyText = null;
  if (!loading && groups.length === 0 && errors.length === 0) {
    if (activeChip) emptyText = tn('nothingFromModule', { module: moduleLabel(activeChip) });
    else if (tab === 'needs') emptyText = tn('nothingNeedsYou');
    else emptyText = tn('caughtUp');
  }

  const bellLabel = unreadCount > 0
    ? tn('bellLabelUnread', { n: badge })
    : tn('title');

  return (
    <div className="hdr-notif" ref={wrapRef}>
      <button
        ref={bellRef}
        type="button"
        className={`hdr-notif-bell${open ? ' is-open' : ''}`}
        aria-label={bellLabel}
        aria-expanded={open}
        aria-controls="hdr-notif-panel"
        aria-haspopup="dialog"
        onClick={toggle}
      >
        <FeatherIcon name="bell" size={18} aria-hidden="true" />
        {badge && <span className="hdr-notif-badge" aria-hidden="true">{badge}</span>}
      </button>

      {open && (
        <section
          id="hdr-notif-panel"
          className="hdr-notif-panel"
          role="dialog"
          aria-labelledby="hdr-notif-title"
          tabIndex={-1}
          ref={panelRef}
        >
          <div className="hdr-notif-head">
            <h2 id="hdr-notif-title">{tn('title')}</h2>
            <button
              type="button"
              className="hdr-notif-markall"
              onClick={onMarkAll}
              disabled={!canMark || marking}
            >
              {marking ? tn('markingRead') : tn('markAllRead')}
            </button>
          </div>

          <div className="hdr-notif-tabs" role="tablist" aria-label={tn('showLabel')}>
            {[
              { id: 'all', label: tn('tabAll'), n: unreadCount },
              { id: 'needs', label: tn('tabNeedsYou'), n: needsCount },
            ].map((tb) => (
              <button
                key={tb.id}
                ref={(el) => { tabRefs.current[tb.id] = el; }}
                type="button"
                role="tab"
                id={`hdr-notif-tab-${tb.id}`}
                aria-selected={tab === tb.id}
                aria-controls="hdr-notif-list"
                tabIndex={tab === tb.id ? 0 : -1}
                className={`hdr-notif-tab${tab === tb.id ? ' is-active' : ''}`}
                onClick={() => setTab(tb.id)}
                onKeyDown={onTabKey}
              >
                {tb.label}
                <span className="hdr-notif-tab-n">{tb.n}</span>
              </button>
            ))}
          </div>

          {chips.length > 1 && (
            <div className="hdr-notif-chips" role="group" aria-label={tn('filterLabel')}>
              {[{ id: 'all', label: tn('filterAll') }, ...chips.map((m) => ({ id: m.id, label: moduleLabel(m) }))]
                .map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    className={`hdr-notif-chip${moduleFilter === c.id ? ' is-active' : ''}`}
                    aria-pressed={moduleFilter === c.id}
                    onClick={() => setModuleFilter(c.id)}
                  >
                    {c.label}
                  </button>
                ))}
            </div>
          )}

          <div
            className="hdr-notif-scroll"
            id="hdr-notif-list"
            role="tabpanel"
            aria-labelledby={`hdr-notif-tab-${tab}`}
          >
            {errors.map((er) => (
              <div key={er.id} className="hdr-notif-error" role="status">
                <FeatherIcon name="alert-circle" size={14} aria-hidden="true" />
                <span>{er.label}</span>
                <button type="button" onClick={er.retry}>{tn('retry')}</button>
              </div>
            ))}
            {loading && <div className="hdr-notif-loading" role="status">{tn('loading')}</div>}
            {groups.map((g) => (
              <div key={g.id} className="hdr-notif-group">
                <h3 className="hdr-notif-group-title" id={`hdr-notif-g-${g.id}`}>{groupTitle[g.id]}</h3>
                <ul aria-labelledby={`hdr-notif-g-${g.id}`}>{g.items.map(renderRow)}</ul>
              </div>
            ))}
            {emptyText && (
              <div className="hdr-notif-empty">
                <FeatherIcon name="check" size={22} aria-hidden="true" />
                <span>{emptyText}</span>
              </div>
            )}
          </div>

          <div className="hdr-notif-foot">
            <span>{tn('footerScope')}</span>
            <Link to="/alerts" onClick={() => close(false)}>{tn('viewAll')}</Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default NotificationsPanel;
